import { REGEX_VALID_DOMAIN } from '../constants.ts'
import { formatCents } from './general.ts'
import { aboveMaximum, defaultSuffix, formatDuration, menuHours, noDeadlineHint, taskLifecycle } from './taskDeadline.ts'
import type { UsageAxis } from '../models/useUsage'
import type { PlanRow } from '../models/useBilling'
import type { ProvisionedTask } from '../models/useTasks'

/**
 * Compute nodes in the app (nobgp/app#330): the New compute node dialog and the
 * compute node page. Pure rules only, so `npm test` can check them.
 */

// ── the catalogue ───────────────────────────────────────────────────────────

/** One row of `compute_classes`, camelCased. */
export type CatalogueClass = {
  name: string
  cpu: number
  memoryMb: number
  priceCentsPerHour: number
  offered: boolean
}

/**
 * The sizes the dialog offers, in this order.
 *
 * ⚠⚠ THE PRICES AND SHAPES COME FROM `compute_classes`, NEVER FROM HERE. This list
 * only chooses WHICH classes the dialog shows. The router prices `xlarge` but
 * cannot place it yet (the cluster instances register 2048 CPU units, so a
 * 4096-unit task fits nowhere), so offering it would give a button whose only
 * result is a failed provision. Add `xlarge` here when the cluster can run it.
 */
export const DIALOG_CLASS_NAMES: readonly string[] = ['small', 'medium', 'large']

/**
 * The catalogue rows the dialog shows.
 *
 * ⚠ A class the catalogue does not have, or has with `offered = false`, is left
 * out. The router refuses a class it does not sell, so a button for it can only
 * fail.
 */
export const dialogSizes = (catalogue: readonly CatalogueClass[]): CatalogueClass[] =>
  DIALOG_CLASS_NAMES.flatMap(name => {
    const row = catalogue.find(c => c.name === name && c.offered)
    return row ? [row] : []
  })

const trim = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100))

/**
 * `0.25 vCPU · 0.5 GiB`. ECS counts CPU in units of 1/1024 vCPU and memory in MiB.
 */
export const sizeShapeLabel = (c: Pick<CatalogueClass, 'cpu' | 'memoryMb'>): string =>
  `${trim(c.cpu / 1024)} vCPU · ${trim(c.memoryMb / 1024)} GiB`

/** `$0.02/h`. */
export const priceLabel = (centsPerHour: number): string => `${formatCents(centsPerHour)}/h`

// ── stop after ──────────────────────────────────────────────────────────────

/**
 * One choice in the Stop after menu.
 *
 * `hours` is what the router's `max_hours` receives. `0` means NO deadline, and
 * the router accepts it only where `plans.compute_deadline_optional` is true.
 */
export type StopAfterOption = {
  key: string
  hours: number
  label: string
  /** Shown but not selectable: the plan does not allow it. `hint` says where it is. */
  disabled?: boolean
  hint?: string
}

/**
 * The option the dialog selects first: the plan's default hours. 24 hours (as in the wireframe)
 * while the plan is not loaded or has no default.
 */
export const defaultStopAfter = (defaultHours: number | null): string => String(defaultHours ?? 24)

const hoursLabel = (hours: number): string =>
  hours >= 48 && hours % 24 === 0 ? `${hours / 24} days` : `${hours} hours`

export const stopAfterOptions = (
  allowNoDeadline: boolean,
  plans: readonly PlanRow[] = [],
  defaultHours: number | null = null,
  maxHours: number | null = null,
): StopAfterOption[] => {
  const hint = allowNoDeadline ? undefined : noDeadlineHint(plans)
  return [
    // ⚠ No "My plan's default" entry (Benoit, 2026-09-15): the plan's default hours are marked
    // "(default)" and selected first (`defaultStopAfter`).
    ...menuHours(defaultHours).map(hours => ({
      key: String(hours),
      hours,
      label: hoursLabel(hours) + defaultSuffix(hours, defaultHours),
      // ⚠ Above the plan's maximum (7 days on Free, router#1549) the router refuses it.
      ...aboveMaximum(hours, maxHours, plans),
    })),
    // ⚠ Always listed, so a Free reader sees what a paid plan adds (Benoit, 2026-09-15). Where the
    // plan does not allow it the entry is DISABLED: the router refuses 0 there, so a selectable
    // entry could only produce a refusal. `validateComputeNodeForm` refuses it too.
    allowNoDeadline
      ? { key: 'none', hours: 0, label: 'No time limit' }
      : {
          key: 'none',
          hours: 0,
          label: 'No time limit',
          disabled: true,
          ...(hint ? { hint } : {}),
        },
  ]
}

// ── who can use the new node ────────────────────────────────────────────────

/**
 * A network's access (router `docs/PERMISSIONS.md` § 4.1, D-20; `networks.access`, migration
 * 0219). Org (the default): every member of the org can use the network's nodes that follow it.
 * Private: only each node's controllers and its access list.
 */
export type NetworkAccess = 'org' | 'private'

export const isNetworkAccess = (value: unknown): value is NetworkAccess => value === 'org' || value === 'private'

/** The one word for Private, everywhere in the app (D-25): Private never excludes the org's Owners and Admins. */
export const PRIVATE_LABEL = 'Private (you and org admins)'

/** The label of the box in the dialog. ⚠ D-25: it says "(you and org admins)" too. */
export const KEEP_PRIVATE_LABEL = 'Keep this node private (you and org admins)'

/** What a new node in a network of this access is, when it follows the network. */
export const NETWORK_ACCESS_LABEL: Record<NetworkAccess, string> = {
  org: 'Shared with the org',
  private: PRIVATE_LABEL,
}

/**
 * The caption under "Keep this node private", for the box and the network's access (null: not
 * known). ⚠ The box is on in every network (§ 4.1: whoever creates the node can set it to Private).
 * In a Private network it changes nothing today, but a node set to Private stays private when the
 * network changes to Org, and a node that follows the network opens with it.
 */
export const keepPrivateCaption = (keepPrivate: boolean, network: NetworkAccess | null): string => {
  if (keepPrivate)
    return `The node starts ${PRIVATE_LABEL}: only you, the org owners and admins, and the people you add can use it. It still reaches the services of the network. It stays ${PRIVATE_LABEL} if the network changes.`
  if (network === 'org')
    return 'This network is shared with the org. The node follows it, so every member of the org can use it (never as root).'
  if (network === 'private')
    return `This network is ${PRIVATE_LABEL}. The node follows it, so it is ${PRIVATE_LABEL} while the network is. If the network changes to Org, every member of the org can use the node.`
  return 'The node follows its network. If the network is shared with the org, every member of the org can use the node (never as root).'
}

// ── the dialog form ─────────────────────────────────────────────────────────

export type ComputeNodeForm = {
  name: string
  size: string
  stopAfter: string
  /** The network's id. The dialog sends the id, never the name (see `provisionArgs`). */
  networkId: string
  /** Keep the new node Private (you and org admins). Omitted or false: it follows its network. */
  keepPrivate?: boolean
  /** The chosen network's access, when it is known. The caption shows it. */
  networkAccess?: NetworkAccess | null
}

export type ComputeNodeFormErrors = Partial<Record<keyof ComputeNodeForm, string>>

/**
 * The name the router will receive. The router lower-cases node names, so the
 * dialog shows and validates the same form.
 */
export const normalizeNodeName = (name: string): string => name.trim().toLowerCase()

/**
 * What is wrong with the form, field by field. An empty object means the form can
 * be sent.
 *
 * ⚠ These checks only save a round trip. The router validates the name, the
 * class and the deadline again, and its refusal is what the dialog shows when it
 * disagrees.
 */
export const validateComputeNodeForm = (
  form: ComputeNodeForm,
  sizes: readonly CatalogueClass[],
  options: readonly StopAfterOption[],
): ComputeNodeFormErrors => {
  const errors: ComputeNodeFormErrors = {}
  const name = normalizeNodeName(form.name)
  if (!name) errors.name = 'Enter a name.'
  else if (!REGEX_VALID_DOMAIN.test(name))
    errors.name = 'Use letters, digits and hyphens, at most 63 characters. Do not start or end with a hyphen.'
  if (!sizes.some(s => s.name === form.size)) errors.size = 'Select a size.'
  if (!options.some(o => o.key === form.stopAfter && !o.disabled)) errors.stopAfter = 'Select when the machine stops.'
  if (!form.networkId) errors.networkId = 'Select a network.'
  return errors
}

/**
 * The `provision_node` arguments for a valid form. Field names are the router's
 * (`docs/openapi.json`, `ProvisionNodeRequest`).
 *
 * ⚠⚠ THE NETWORK GOES BY ID, AND ONLY BY ID. A network name can exist in two of the
 * user's organizations, and the router resolves a name to the personal organization
 * first, so a name could start a billed machine in a network the reader is not
 * looking at. `network_id` names exactly one network (nobgp-router#1480, PR #1485).
 * Do not add `network_name` beside it: the router refuses both fields together with
 * `invalid_args`, even when they name the same network.
 */
export const provisionArgs = (
  form: ComputeNodeForm,
  options: readonly StopAfterOption[],
): Record<string, unknown> => {
  const option = options.find(o => o.key === form.stopAfter)
  return {
    network_id: form.networkId,
    node_name: normalizeNodeName(form.name),
    class: form.size,
    ...(option ? { max_hours: option.hours } : {}),
    ...startingAccessArg(form.keepPrivate),
  }
}

/**
 * The `access` argument of `provision_node` (router PR #1856, D-20), or nothing.
 *
 * ⚠ A node that follows its network is sent as NO field. `follow` is the router's default, and a
 * router before PR #1856 refuses an unknown field with `invalid_args`, so the default must still
 * start a machine there. `private` on such a router is refused, which is correct: the machine must
 * not start shared when the reader asked for private.
 *
 * ⚠ The box sends `private` in a Private network too: the node then stays private when the
 * network changes to Org (§ 4.1).
 */
export const startingAccessArg = (keepPrivate: boolean | undefined): { access?: 'private' } =>
  keepPrivate ? { access: 'private' } : {}

/**
 * The line the result shows about the node's access, or null.
 *
 * ⚠ A re-adopted node KEEPS the setting it has: the router writes `access` once, at creation, and
 * does not apply it to an existing node. The router says so in a note on the result envelope,
 * which `callTool` does not return, so the dialog says it from `readopted_node_id`.
 */
export const startingAccessResultLine = (sentPrivate: boolean, readoptedNodeId: string | undefined): string | null => {
  if (!sentPrivate) return null
  if (readoptedNodeId)
    return `This node already existed, so it keeps its access setting. "${KEEP_PRIVATE_LABEL}" was not applied.`
  return `The node is ${PRIVATE_LABEL}.`
}

/**
 * The included-compute line under the form, or null when there is nothing true to
 * say.
 *
 * ⚠⚠ MONEY, AND FROM THE SAME SOURCE AS THE USAGE PAGE'S COMPUTE BAR: the
 * `compute` axis of `get_org_usage` (`useUsage.axes`). Its `included` is what the
 * org was GRANTED for the period, so a launch promotion counts. The plan row's
 * `compute_allowance_cents` would leave the promotion out and print a smaller
 * number than `/usage` on the same org.
 *
 * ⚠ No hours and no small-machine footnote (app#327): the dialog sells several
 * sizes, and one dollar amount is true for all of them.
 *
 * ⚠ What happens after the allowance is used follows the plan CLASS, not its name:
 * a hard-capped plan (Free) stops the machines, a paid plan draws the credit
 * balance. The exempt tier has `included` null and gets no line. An axis that is
 * not in cents is not read at all, rather than printed in the wrong unit.
 */
export const includedComputeLine = (
  plan: Pick<PlanRow, 'name' | 'hardCaps' | 'metered'> | null,
  axis: Pick<UsageAxis, 'used' | 'included' | 'unit'> | undefined,
): string | null => {
  if (!plan || !axis || axis.unit !== 'cents' || axis.included === null) return null
  const money = (cents: number) => (cents > 0 && cents < 0.5 ? '< $0.01' : formatCents(cents))
  const left = money(Math.max(0, axis.included - axis.used))
  const after = plan.hardCaps
    ? 'When it is used up, your machines stop.'
    : plan.metered
      ? 'After that, compute is paid from your credit balance.'
      : ''
  const planName = plan.name.charAt(0).toUpperCase() + plan.name.slice(1)
  return `${planName}: ${left} of ${money(axis.included)} included compute left this month. ${after}`.trim()
}

// ── the node page ───────────────────────────────────────────────────────────

/**
 * Per-task facts only the database can price, from `get_node_tasks`
 * (nobgp-router#1466, router PR #1472, migration 0191).
 *
 * ⚠ `costCents` is NULL for an off-catalogue shape and means UNPRICED, never
 * free. It is 0 for a task that is not billable.
 * ⚠ `billable` false means the task already existed at go-live and was charged
 * nothing. It reads `not charged`, which is a different fact from a cost of zero.
 * ⚠ `stopReason` is null for a running task, and for a task that ended before
 * migration 0191 recorded reasons.
 */
export type NodeTaskExtra = {
  costCents: number | null
  billable: boolean
  billedMinutes: number
  stopReason: string | null
}

/**
 * Whether a PostgREST error means the RPC is not deployed yet.
 *
 * ⚠ ONLY this case turns the cost and reason columns off quietly. Any other error
 * (for example `42501`, an unknown node or a caller outside the org) is a real
 * failure, and the caller logs it. `PGRST202` is "function not found in the
 * schema cache"; a 404 is how PostgREST answers the same thing on some versions.
 *
 * ⚠ The 404 is the RESPONSE's `status`, passed separately. A `PostgrestError` has
 * no `status` field, so a check on `error.status` could never be true.
 */
export const isMissingRpc = (error: { code?: string } | null | undefined, status?: number): boolean =>
  !!error && (error.code === 'PGRST202' || status === 404)

const numberOrNull = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

/**
 * The RPC rows keyed by `task_id`.
 *
 * Column names are the router's (`get_node_tasks` in router PR #1472). `numeric`
 * and `bigint` columns can arrive from PostgREST as strings, so both forms are
 * read. A row with no `task_id` is skipped. Only an explicit `billable: false`
 * reads as not charged.
 */
export const parseNodeTaskExtras = (data: unknown): Map<string, NodeTaskExtra> => {
  const map = new Map<string, NodeTaskExtra>()
  if (!Array.isArray(data)) return map
  for (const raw of data) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    if (typeof row.task_id !== 'string' || !row.task_id) continue
    map.set(row.task_id, {
      costCents: numberOrNull(row.cost_cents),
      billable: row.billable !== false,
      billedMinutes: numberOrNull(row.billed_minutes) ?? 0,
      stopReason: typeof row.stop_reason === 'string' && row.stop_reason ? row.stop_reason : null,
    })
  }
  return map
}

/**
 * How a task ended, in words.
 *
 * ⚠ The router's vocabulary (`user`, `user_stop`, `deadline`, `allowance`,
 * `credit_empty`, `lapsed`, `provider`) can grow. An unknown reason reads `ended`,
 * never the raw value and never a guess.
 *
 * ⚠⚠ `user` AND `user_stop` ARE DIFFERENT ACTS. `user` is Terminate
 * (`deprovision_node`): the node is removed. `user_stop` is Stop (`task_stop`,
 * router migration 0194): the node is kept offline. Both are a person's choice, so
 * both must not read the same.
 */
export const stopReasonLabel = (reason: string | null): string => {
  switch (reason) {
    case 'user':
      return 'terminated'
    case 'user_stop':
      return 'stopped'
    case 'deadline':
      return 'deadline'
    case 'allowance':
    case 'credit_empty':
    case 'lapsed':
      return 'stopped for billing'
    case 'provider':
      return 'stopped by the provider'
    default:
      return 'ended'
  }
}

/** Which billing stop it was, for the three reasons that share `stopped for billing`. */
export const stopReasonHint = (reason: string | null): string | null => {
  switch (reason) {
    case 'allowance':
      return 'allowance used up'
    case 'credit_empty':
      return 'credit empty'
    case 'lapsed':
      return 'subscription lapsed'
    default:
      return null
  }
}

export type NodeTaskRow = {
  id: string
  startedAt: Date
  endedAt: Date | null
  size: string
  /** Wall-clock time from start to end, or to `now` for a running task. */
  durationMs: number
  deadlineAt: Date | null
  /** `running`, `stopping` (past its deadline, not swept yet), or `ended`. */
  state: 'running' | 'stopping' | 'ended'
  /** `undefined` when the RPC is not available. `null` when the task is unpriced. */
  costCents?: number | null
  /** `undefined` when the RPC is not available or has no row for this task. */
  billable?: boolean
  /** `undefined` when the RPC is not available. */
  stopReason?: string | null
}

/**
 * One row of the node's task list.
 *
 * `extras` is null when `get_node_tasks` is not available. Then the row carries NO
 * cost and NO reason, and the table leaves both columns out.
 */
export const nodeTaskRow = (
  task: ProvisionedTask,
  sizeLabel: string,
  now: Date,
  extras: Map<string, NodeTaskExtra> | null,
): NodeTaskRow => {
  const lifecycle = taskLifecycle(task, now)
  const startedAt = new Date(task.createdAt)
  const endedAt = lifecycle.endedAt
  const extra = extras?.get(task.id)
  return {
    id: task.id,
    startedAt,
    endedAt,
    size: sizeLabel,
    durationMs: Math.max(0, (endedAt ?? now).getTime() - startedAt.getTime()),
    deadlineAt: lifecycle.deadlineAt,
    state: lifecycle.state === 'ended' ? 'ended' : lifecycle.state === 'expiring' ? 'stopping' : 'running',
    ...(extras
      ? extra
        ? { costCents: extra.costCents, billable: extra.billable, stopReason: extra.stopReason }
        : { costCents: null, stopReason: null }
      : {}),
  }
}

/** Newest first, by start time. */
export const sortNodeTaskRows = (rows: NodeTaskRow[]): NodeTaskRow[] =>
  [...rows].sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())

/** The Status column. A billing stop names which one: `stopped for billing (credit empty)`. */
export const nodeTaskStatus = (row: NodeTaskRow): string => {
  if (row.state === 'running') return 'running'
  if (row.state === 'stopping') return 'stopping'
  const reason = row.stopReason ?? null
  const hint = stopReasonHint(reason)
  return hint ? `${stopReasonLabel(reason)} (${hint})` : stopReasonLabel(reason)
}

/**
 * The Cost column.
 *
 * ⚠ `billable: false` reads `not charged`. `null` is UNPRICED and reads
 * `unpriced`, never `$0.00`. `undefined` means the column is not shown at all.
 */
export const nodeTaskCost = (row: NodeTaskRow): string =>
  row.billable === false
    ? 'not charged'
    : row.costCents === null || row.costCents === undefined
      ? 'unpriced'
      : row.costCents > 0 && row.costCents < 0.5
        ? '< $0.01'
        : formatCents(row.costCents)

/**
 * `Running 2h 18m · stops in 21h 42m · $0.05 so far` for the open task.
 *
 * ⚠ The cost part is left out when the RPC is not available, and the deadline part
 * says `no time limit` when there is none.
 */
export const computeHeaderLine = (row: NodeTaskRow, now: Date): string => {
  const parts = [`Running ${formatDuration(row.durationMs)}`]
  if (row.deadlineAt === null) parts.push('no time limit')
  else {
    const remaining = row.deadlineAt.getTime() - now.getTime()
    parts.push(remaining > 0 ? `stops in ${formatDuration(remaining)}` : 'past its deadline, stopping')
  }
  if (row.costCents !== undefined) parts.push(row.billable === false ? 'not charged' : `${nodeTaskCost(row)} so far`)
  return parts.join(' · ')
}

// ── Stop ────────────────────────────────────────────────────────────────────

/**
 * The router tool that stops a machine and KEEPS its node (nobgp/app#337,
 * nobgp-router#1479).
 *
 * ⚠⚠ NOT `deprovision_node`. That tool also removes the node from the directory,
 * which is Terminate. A Stop button that calls it does something else than its name.
 */
export const TASK_STOP_TOOL = 'task_stop'

/**
 * The client wait for `task_stop`, in seconds.
 *
 * ⚠ The router waits up to 35 s for the node to read offline before it answers.
 * `callTool` sets no timeout of its own, so this number is what the page TELLS the
 * reader, and it must stay above the router's bound.
 */
export const TASK_STOP_WAIT_SECONDS = 40

/** The `task_stop` payload (router PR #1487), camelCased. */
export type TaskStopOutcome = {
  taskId: string
  nodeId: string | null
  nodeName: string | null
  alreadyStopped: boolean
  stoppedAt: Date | null
  /** Null when the router did not wait: already stopped, no node, or a newer task owns the node. */
  nodeOffline: boolean | null
  /**
   * Whether a new compute node with `nodeName` starts THIS node again (router#1504). False for a
   * revoked node, for example. Null when the router did not say: no node, the answer waits for the
   * node to show offline, or a router older than the field.
   */
  restartableByName: boolean | null
}

/**
 * Reads a `task_stop` payload. `callTool` has already removed the envelope.
 *
 * ⚠ A missing or malformed payload is not success with defaults: the router answered
 * 2xx, so the stop happened, but nothing here can say more than that. The caller
 * gets `taskId` back and nulls for the rest.
 */
export const parseTaskStopPayload = (taskId: string, data: unknown): TaskStopOutcome => {
  const row = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : {}
  const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
  const stoppedAtRaw = str(row.stopped_at)
  const stoppedAt = stoppedAtRaw ? new Date(stoppedAtRaw) : null
  return {
    taskId: str(row.task_id) ?? taskId,
    nodeId: str(row.node_id),
    nodeName: str(row.node_name),
    alreadyStopped: row.already_stopped === true,
    stoppedAt: stoppedAt && !Number.isNaN(stoppedAt.getTime()) ? stoppedAt : null,
    nodeOffline: typeof row.node_offline === 'boolean' ? row.node_offline : null,
    restartableByName: typeof row.restartable_by_name === 'boolean' ? row.restartable_by_name : null,
  }
}

/** The shape of `callTool`, so the stop can be tested without the network. */
export type ToolCaller = (name: string, args: Record<string, unknown>) => Promise<unknown>

/**
 * Stops one task through the router. `useTasks.stop` calls this with `callTool`.
 *
 * ⚠ A router refusal is THROWN, unchanged. The caller shows its message.
 */
export const runTaskStop = async (call: ToolCaller, taskId: string): Promise<TaskStopOutcome> =>
  parseTaskStopPayload(taskId, await call(TASK_STOP_TOOL, { task_id: taskId }))

export type StopNotice = { severity: 'success' | 'warning' | 'info'; text: string }

/**
 * What the node page says after Stop returns.
 *
 * ⚠⚠ `nodeOffline: false` IS A WARNING, NOT A FAILURE. The machine stopped. But a
 * new compute node with this name, created while the node still reads online, gets
 * a DIFFERENT name, a new node and empty node storage (router#1123). The reader must
 * wait, so the page says so.
 *
 * ⚠⚠ THE WAIT WARNING DEPENDS ON `nodeOffline` ONLY, NOT ON `alreadyStopped`. The router
 * sets `already_stopped` also when another stop closed the task while this call ran, and
 * when the provider had already stopped the container. In both cases it still waits for
 * the node, and `node_offline` can be false. An `alreadyStopped` check first would drop
 * the warning in exactly those cases.
 *
 * ⚠ `already_stopped` is success too: the machine is in the state the reader asked
 * for. The notice does not say "nothing changed", because in the provider case this
 * call recorded the stop. `formatTime` prints `stoppedAt`.
 *
 * ⚠ `nodeOffline: null` with a node means the router did not wait. One case is a newer
 * task that owns the node now, and that node is online. So the notice says the node is
 * kept, not that it stays offline, and it does not tell the reader to start it again.
 *
 * ⚠⚠ `restartableByName: false` REMOVES EVERY "START IT AGAIN BY NAME" (router#1504). For a revoked
 * node, for example, the router refuses the name, or the name gets a different node. The labels,
 * role grants and node storage are then not used. So the notice does not say "create a compute node
 * with the name" to start it, and it does not say "wait until it shows offline" either, because
 * waiting does not start this node. `null` (an older router, or no answer yet) keeps the text that
 * was there before the field.
 *
 * ⚠ The text says "now", as the router's own note does. `false` is not only a revoked node: an
 * open or unregistered task that holds the name, or a contested name, also gives `false`, and those
 * causes can clear. So the notice must not say the node can never start again by its name.
 */
export const stopNotice = (
  outcome: TaskStopOutcome,
  fallbackName: string,
  formatTime: (d: Date) => string,
): StopNotice => {
  const name = outcome.nodeName ?? fallbackName
  const noRestart =
    `You cannot start this node again by its name now. A new compute node with the name ${name} does not start this node now: ` +
    'the name is refused, or it makes a new node that does not get these labels, role grants and node storage.'
  const wait =
    outcome.restartableByName === false
      ? noRestart
      : `Wait until it shows offline before you create a compute node with the name ${name}. ` +
        'If you do not wait, the new machine can get a different name and empty node storage.'
  if (outcome.alreadyStopped) {
    const when = outcome.stoppedAt ? ` at ${formatTime(outcome.stoppedAt)}` : ''
    const text = `This machine had already stopped${when}. This press did not stop it.`
    if (outcome.nodeOffline === false) {
      return { severity: 'warning', text: `${text} The node ${name} does not show offline yet. ${wait}` }
    }
    return { severity: 'info', text }
  }
  if (outcome.nodeOffline === false) {
    return {
      severity: 'warning',
      text: `The machine stopped, but the node ${name} does not show offline yet. ${wait}`,
    }
  }
  if (!outcome.nodeId) {
    return { severity: 'success', text: 'The machine stopped. It had no node to keep.' }
  }
  if (outcome.nodeOffline === null) {
    return { severity: 'success', text: `The machine stopped. The node ${name} is kept with its labels and node storage.` }
  }
  if (outcome.restartableByName === false) {
    return {
      severity: 'success',
      text: `The machine stopped. The node ${name} stays offline with its labels and node storage. ${noRestart}`,
    }
  }
  return {
    severity: 'success',
    text:
      `The machine stopped. The node ${name} stays offline with its labels and node storage. ` +
      `To start it again, create a compute node with the name ${name}.`,
  }
}
